import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Theme,
  TableFooter,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { makeStyles, createStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';

import { SHeadTableCell } from '../../../components/styledComponents/Tables';
import Preloader from '../../../components/other/Preloader/Preloader';
import AlertDialog from '../../../components/other/Dialog/AlertDialog';
import homeStyles from '../../../constants/homeStyles';
import { setLayoutFooter, setLayoutSubheader } from '../../../utils/layout';
import { useGetManufacturer } from './hooks/useGetManufacturer';
import TablePaginator from '../../../components/tableComponents/TablePaginator/TablePaginator';
import ManufacturerModal, { ModalMethods } from './components/ManufacturerModal';
import { useDeleteManufacture } from './hooks/useDeleteManufacture';
import { IAppState } from '../../../store/rootDuck';
import { ManufacturersSearchField } from './components/ManufacturersSearchField';

export const Manufacturers = () => {
  const { me } = useSelector((store: IAppState) => ({
    me: store.profile.me,
  }));
  const [deleteId, setDeleteId] = useState<number | undefined>(-1);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { fetch, loading, page, manufacturers } = useGetManufacturer();
  const modalRef = useRef<ModalMethods>(null);
  const [modalId, setModalId] = useState<number | undefined>();
  const { handleManufacture: handleDelete, success: successDelete } = useDeleteManufacture();

  const homeClasses = homeStyles();
  const classes = useStyles();
  const intl = useIntl();

  useEffect(() => {
    fetch({ page: 1, per_page: 20 });
  }, []);

  useEffect(() => {
    successDelete && fetch({ page: 1, per_page: 20, text: search });
  }, [successDelete]);

  setLayoutSubheader({
    title: 'Производители',
  });
  setLayoutFooter({ show: true });

  const handleSearch = (value?: string) => {
    setSearch(value || '');
    if (value !== search) {
      fetch({ page: 1, per_page: 20, text: value });
    }
  };

  const handleOpenDeleteDialog = useCallback((id: number | undefined) => {
    setDeleteId(id);
    setAlertOpen(true);
  }, []);

  const deleteAction = useCallback(() => {
    setAlertOpen(false);
    if (deleteId) {
      handleDelete(deleteId);
    }
  }, [deleteId]);

  if (!me?.is_admin) return <Preloader />;

  return (
    <>
      <AlertDialog
        open={isAlertOpen}
        message='Вы уверены, что хотите удалить производителя?'
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          setAlertOpen(false);
        }}
        handleAgree={deleteAction}
      />

      <Box
        display='flex'
        justifyContent='space-between'
        gap='12px'
        flexDirection='row'
        alignItems='center'
      >
        <Button color='primary' variant='contained' onClick={() => modalRef.current?.onOpen()}>
          Добавить
        </Button>
        <ManufacturersSearchField handleSearch={handleSearch} search={search} />
      </Box>

      {loading ? (
        <Preloader />
      ) : (
        <Row>
          <Col>
            {!manufacturers || !manufacturers.length ? (
              <Typography className={classes.empty} component='h5' variant='h5'>
                {intl.formatMessage({ id: 'COMMON.LIST.EMPTY' })}
              </Typography>
            ) : (
              <>
                <Card className={classes.card}>
                  <CardContent className={homeClasses.classes.tableContainer}>
                    <Table aria-label='simple table'>
                      <TableHead>
                        <TableRow>
                          <SHeadTableCell className={classes.rowItems}>
                            {intl.formatMessage({ id: 'PRODUCT.TABLE.ID' })}
                          </SHeadTableCell>
                          <SHeadTableCell className={classes.rowItems}>
                            Уникальное наименование
                          </SHeadTableCell>
                          <SHeadTableCell className={classes.rowItems}>
                            Отображаемое наименование
                          </SHeadTableCell>
                          <SHeadTableCell className={classes.rowItems}>
                            Английское наименование
                          </SHeadTableCell>
                          <SHeadTableCell align='right' className={classes.rowItems}>
                            {intl.formatMessage({ id: 'PRODUCT.TABLE.ACTIONS' })}
                          </SHeadTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {manufacturers.map(item => (
                          <TableRow key={item.id}>
                            <TableCell component='th' scope='row' className={classes.rowItems}>
                              {item.id || '-'}
                            </TableCell>
                            <TableCell className={classes.rowItems}>
                              {item.name || '-'}
                            </TableCell>
                            <TableCell className={classes.rowItems}>
                              {item.print_name || '-'}
                            </TableCell>
                            <TableCell className={classes.rowItems}>
                              {item.english_name || '-'}
                            </TableCell>
                            <TableCell align='right'>
                              <IconButton
                                color='primary'
                                aria-label='previous'
                                onClick={() => {
                                  setModalId(item.id);
                                  modalRef.current?.onOpen();
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                onClick={() => {
                                  handleOpenDeleteDialog(item.id);
                                }}
                              >
                                <DeleteIcon color='error' />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TablePaginator
                            page={page.page}
                            perPage={page.perPage}
                            total={page.total}
                            fetchRows={(data: any) =>
                              fetch({ page: data.page, per_page: data.perPage, text: search })
                            }
                            label='Производителей на странице'
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </CardContent>
                </Card>
              </>
            )}
          </Col>
        </Row>
      )}

      <ManufacturerModal
        modalRef={modalRef}
        id={modalId}
        update={() => fetch({ page: 1, per_page: 20, text: search })}
        onClose={() => setModalId(undefined)}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    empty: {
      marginBottom: 20,
      marginTop: 20,
    },
    buttons: {
      marginRight: theme.spacing(3),
    },
    card: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    categoryTree: {
      maxWidth: 400,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    img: {
      width: 50,
      height: 50,
    },
    rowItems: {
      fontSize: '17px !important',
    },
  })
);
